import Modal from '../ui/Modal';
import StepManager from './StepManager';
import TemplateSelector from './TemplateSelector';
import STCCanvas from '../ui/STCCanvas';
import StepController from './StepController';
import PriceCalculator from './PriceCalculator';
import PriceDisplay from '../ui/PriceDisplay';
import CrossSideSteps from '../ui/CrossSideSteps';
import DesignExporter from './DesignExporter';
import { loadAllFonts } from '../utils/fontLoader';
import InfoIconManager from '../ui/InfoIconManager';
import NotificationPopup from '../ui/NotificationPopup';

/**
 * Main designer class
 */
export default class Designer {
    
    /**
     * Create a new Designer
     * 
     * @param {HTMLElement} button - Designer button
     * @param {object} options - Designer options
     */
    constructor(button, options = {}) {
        this.button = button;
        this.options = {
            basePrice: 29.99,
            templates: [],
            defaultTemplate: null,
            ...options
        };

        this.modal = new Modal();
        this.currentState = 'template-selection'; // or 'customization'
        this.template = null;
        this.canvas = null;
        this.stepManager = null;
        this.stepController = null;

        this.hasVisitedBackSide = false;
        this.backSideNotification = null;
        
        // Initialize price calculator
        this.priceCalculator = new PriceCalculator({
            basePrice: this.options.basePrice
        });

        document.addEventListener('stc:image:needsBackgroundRemovalHelp', (e) => {
            this.showBackgroundRemovalNotification(e.detail);
        });
        
        loadAllFonts();

        this.bindEvents();
    }

    /**
     * Bind event listeners
     */
    bindEvents() {
        // Check if we're in edit mode on page load
        const urlParams = new URLSearchParams(window.location.search);
        const editCartItemKey = urlParams.get('edit_cart_item');

        if (editCartItemKey) {
            // Detect mode first before opening modal
            console.log('🎨 [EDIT_MODE] Edit mode detected, checking cart data...');
            setTimeout(async () => {
                try {
                    const cartItemData = await this.fetchCartItemData(editCartItemKey);
                    const isSimpleForm = cartItemData?.template?.exportMetadata?.source === 'simple_form';

                    console.log('🔍 [EDIT_MODE] Mode detected:', isSimpleForm ? 'SIMPLE FORM' : 'DESIGNER');

                    if (isSimpleForm) {
                        // Load in simple form mode WITHOUT opening modal
                        console.log('✅ [EDIT_MODE] Loading simple form mode');
                        await this.loadInSimpleFormMode(editCartItemKey, cartItemData);
                    } else {
                        // Open designer modal for canvas mode
                        console.log('✅ [EDIT_MODE] Opening designer modal');
                        this.modal.show();
                    }
                } catch (error) {
                    console.error('❌ [EDIT_MODE] Error detecting mode:', error);
                    // Fallback: open designer
                    this.modal.show();
                }
            }, 100);
        }

        // Open designer on button click
        this.button.addEventListener('click', () => {
            this.modal.show();
        });

        // Initialize on first show
        this.modal.on('show', () => {
            if (!this.templateSelector) {
                this.initialize();
            }
        });

        // Handle modal close - intercept BEFORE it closes
        this.modal.on('beforeHide', (e) => {
            if (this.currentState === 'customization') {
                // Ask for confirmation if in customization
                if (confirm('Are you sure you want to exit? Your changes will be lost.')) {
                    this.resetDesigner();
                } else {
                    e.preventDefault(); // Cancels the hide — modal stays open
                }
            }
        });

    }

    /**
     * Initialize designer
     */
    async initialize() {
        let modalContainer = this.modal.modal.querySelector('.stc-modal-container');

        // Check if we're in edit mode (Designer mode only, Simple Form handled in bindEvents)
        const urlParams = new URLSearchParams(window.location.search);
        const editCartItemKey = urlParams.get('edit_cart_item');

        if (editCartItemKey) {
            // If we're here, it means the modal was opened, so it's Designer mode
            console.log('🎨 [INITIALIZE] Loading cart item in Designer mode:', editCartItemKey);
            await this.loadFromCartItem(editCartItemKey);
        } else {
            // Normal flow: show template selector
            this.templateSelector = new TemplateSelector(
                modalContainer,
                this.options.templates
            );

            // Listen for template selection
            modalContainer.addEventListener('stc:template:selected', (e) => {
                this.handleTemplateSelected(e.detail.template);
            });

            // Initialize other components as hidden
            this.initializeComponents();
        }
    }

    /**
     * Initialize UI components
     */
    initializeComponents() {
        // Hide side switch and steps initially
        const sideSwitch = this.modal.modal.querySelector('.stc-side-switch');
        if (sideSwitch) sideSwitch.style.display = 'none';

        // Initialize step manager (hidden)
        const stepsContainer = this.modal.modal.querySelector('.stc-steps');
        if (stepsContainer) stepsContainer.style.display = 'none';

        // Handle side changes
        this.modal.on('sideChange', ({ detail }) => {
            this.handleSideChange(detail);
        });
    }

    /**
     * Handle template selection
     * 
     * @param {object} template - Template data
     */
    async handleTemplateSelected(template) {
        try {
            this.template = template;
            this.currentState = 'customization';

            // Handle config - it might already be parsed (from edit data) or be a JSON string (from template)
            if (typeof this.template.config === 'string') {
                this.template.config = JSON.parse(this.template.config);
            }
            // If it's already an object, we don't need to parse it

            // Log template data for debugging
            if (this.template.editMode) {
                console.log('📋 [TEMPLATE_LOAD] EDIT MODE - Template loaded:', JSON.parse(JSON.stringify(this.template)));

                // Log client_name text fill color to debug color loading
                if (this.template.config?.front?.elements?.client_name) {
                    console.log('📝 [TEMPLATE_LOAD] Client name element data:', this.template.config.front.elements.client_name);
                    console.log('🎨 [TEMPLATE_LOAD] Client name fill color:', this.template.config.front.elements.client_name.data?.fill);
                }
            } else {
                console.log('📋 [TEMPLATE_LOAD] DEFAULT MODE - Template loaded:', JSON.parse(JSON.stringify(this.template)));
            }

            // Show customization interface
            this.showCustomizationInterface();
    
            // Initialize canvas with template - this loads both sides
            await this.initializeCanvas();
    
            // Initialize step components - now creates handlers for both sides
            this.initializeStepComponents();
            
            // Update price
            this.updatePrice();

            this.updateSlabPreviewForSide(this.canvas.currentSide);
    
        } catch (error) {
            console.error('Error initializing template:', error);
            alert('Failed to initialize template. Please try again.');
        }
    }

    /**
     * Show customization interface
     */
    showCustomizationInterface() {
        // Show side switch
        const sideSwitch = this.modal.modal.querySelector('.stc-side-switch');
        if (sideSwitch) sideSwitch.style.display = '';

        // Show steps
        const stepsContainer = this.modal.modal.querySelector('.stc-steps');
        if (stepsContainer) stepsContainer.style.display = '';

        // Update modal title
        const title = this.modal.modal.querySelector('.stc-modal-title');
        if (title) title.textContent = this.template.title;

        // Enable close button
        const closeBtn = this.modal.modal.querySelector('.stc-modal-close');
        if (closeBtn) closeBtn.style.display = '';
        
        // Initialize price display
        const priceContainer = this.modal.modal.querySelector('.stc-price-summary')?.parentElement;
        if (priceContainer) {
            this.priceDisplay = new PriceDisplay(priceContainer, {
                showDetailedBreakdown: true
            });
            
            // Initialize with base price
            this.priceDisplay.updateDisplay({
                basePrice: this.options.basePrice,
                frontExtras: 0,
                backExtras: 0,
                totalExtras: 0,
                total: this.options.basePrice
            }, false);

            const addToCartBtn = this.modal.modal.querySelector('.stc-add-to-cart');
            if (addToCartBtn) {
                addToCartBtn.addEventListener('click', () => {
                    this.handleAddToCart();
                });
            }
        }
    }

    /**
     * Initialize canvas with template
     */
    async initializeCanvas() {
        const container = this.modal.modal.querySelector('.stc-canvas-container');
    
        // Create canvas if it doesn't exist
        if (!this.canvas) {
            this.canvas = new STCCanvas(container);
        }

        // Load template into canvas (this will create elements for both sides)
        await this.canvas.loadTemplate(this.template.config);
        
        // Side change handler
        this.modal.on('sideChange', ({ detail: side }) => {
            this.handleSideChange(side);
        });
        
        // Set up slab preview event listeners
        this.canvas.on('slabDataUpdate', ({ detail }) => {
            this.handleSlabDataUpdate(detail);
        });
        
        this.canvas.on('slabSideChange', ({ detail }) => {
            this.updateSlabPreviewForSide(detail.side);
        });
    }

    /**
     * Initialize step manager and controller
     */
    initializeStepComponents() {
        // Initialize step manager
        const stepsContainer = this.modal.modal.querySelector('.stc-steps');
        this.stepManager = new StepManager(stepsContainer);

        // Initialize step controller
        this.stepController = new StepController(this.canvas, this.stepManager);

        // Connect components
        this.stepManager.setStepController(this.stepController);

        // IMPORTANT: Load stepsData from edit mode BEFORE initializing handlers
        // This ensures steps without canvas elements (like slab) have their data available
        if (this.template?.editMode && this.template?.stepsData) {
            this.loadStepsDataFromEdit(this.template.stepsData);
        }

        // Initialize cross-side steps component
        this.crossSideSteps = new CrossSideSteps(stepsContainer, this.stepManager);

        // Initialize info icon manager
        this.infoIconManager = new InfoIconManager(stepsContainer, this.stepController);

        // Add a toggle button for cross-side view
        const sideSwitch = this.modal.modal.querySelector('.stc-side-switch');
        if (sideSwitch) {
            const toggleBtn = document.createElement('button');
            toggleBtn.className = 'stc-cross-side-toggle';
            toggleBtn.innerHTML = 'Show All Steps';
            toggleBtn.addEventListener('click', () => {
                const isVisible = this.crossSideSteps.toggle();
                toggleBtn.innerHTML = isVisible ? 'Hide All Steps' : 'Show All Steps';
            });
            sideSwitch.appendChild(toggleBtn);
        }

        // Initialize handlers for both sides
        this.stepController.initializeAllHandlers();
        
        // Handle step completion for pricing
        this.stepManager.on('stepComplete', ({ detail }) => {
            this.updatePrice();
        });
        
        // Handle step changes
        this.stepManager.on('stepDataChange', ({ detail }) => {
            this.updatePrice();
        });
        
        // Connect the price calculator to the step manager for handler access
        this.priceCalculator.setStepManager(this.stepManager);
        
        // Initialize design exporter
        this.designExporter = new DesignExporter(
            this.stepController,
            this.stepManager,
            this.canvas,
            { includeDataUrls: true }
        );

        document.addEventListener('stc:slab:previewUpdate', (event) => {
            // Update slab preview if current side has slab
            this.updateSlabPreviewForSide(this.canvas.currentSide);
        });
    }

    /**
     * Load stepsData from edit mode into stepManager
     * This is used for steps that don't have canvas elements (like slab)
     *
     * @param {object} stepsData - Steps data organized by side
     */
    loadStepsDataFromEdit(stepsData) {
        console.log('📦 [EDIT_MODE] Loading stepsData into stepManager:', stepsData);
        console.log('📦 [EDIT_MODE] stepsData keys - front:', Object.keys(stepsData.front || {}), 'back:', Object.keys(stepsData.back || {}));

        // Load data for each side
        ['front', 'back'].forEach(side => {
            if (stepsData[side]) {
                Object.entries(stepsData[side]).forEach(([stepId, stepData]) => {
                    console.log(`📦 [EDIT_MODE] Setting ${stepId} data for ${side}:`, JSON.stringify(stepData));

                    // Set step data directly into stepManager
                    // Use notifyChanges=false to avoid triggering updates before handlers exist
                    this.stepManager.setStepDataForSide(side, stepId, stepData, false);

                    // Verify data was set correctly
                    const verifyData = this.stepManager.getStepDataForSide(side, stepId);
                    console.log(`📦 [EDIT_MODE] Verified ${stepId} data for ${side}:`, verifyData ? 'OK' : 'FAILED');
                });
            }
        });

        console.log('📦 [EDIT_MODE] StepsData loaded successfully');
    }

    /**
     * Handle side change
     *
     * @param {string} side - 'front' or 'back'
     */
    handleSideChange(side) {
        // Update canvas - this will handle showing/hiding elements
        this.canvas.changeSide(side);
        
        // Update step components
        if (this.stepManager) this.stepManager.changeSide(side);
        
        // Track if user visited back side
        if (side === 'back') this.hasVisitedBackSide = true;
        
        // Always update slab preview when changing sides
        this.updateSlabPreviewForSide(side);
    }

    /**
     * Handle slab data updates
     * 
     * @param {object} data - Slab data update event data
     */
    handleSlabDataUpdate(data) {
        if (!this.canvas) return;

        // Update slab preview state
        this.canvas.setSlabPreviewEnabled(data.enabled);
        
        // Update preview content if enabled
        if (data.enabled) {
            this.canvas.updateSlabPreview(data.slabData, data.side);
        }
    }

    /**
     * Update slab preview for the current side
     * 
     * @param {string} side - Current side ('front' or 'back')
     */
    updateSlabPreviewForSide(side) {
        if (!this.canvas || !this.stepController) return;
        
        // Always check the back side for slab configuration first
        // since that's where the slab step exists
        const slabHandler = this.stepController.getHandlerForSide('front', 'slab');
        
        if (slabHandler) {
            // Get slab data and check if enabled
            const slabData = slabHandler.getSlabDataForPreview();
            
            if (slabData && slabData.enabled === true) {
                // Slab is explicitly enabled, show the preview for the current viewing side
                this.canvas.updateSlabPreview(slabData, side);
                this.canvas.setSlabPreviewEnabled(true);
            } else {
                // No slab enabled
                this.canvas.setSlabPreviewEnabled(false);
            }
        } else {
            // No slab handler
            this.canvas.setSlabPreviewEnabled(false);
        }
    }

    /**
     * Update price based on selected options from both sides
     */
    updatePrice() {
        if (!this.stepManager) return;
        
        // Get all completed steps
        const completedSteps = {
            front: Array.from(this.stepManager.completedSteps.front),
            back: Array.from(this.stepManager.completedSteps.back)
        };
        
        // Get all steps data
        const stepsData = this.stepManager.getAllStepsData();
        
        // Calculate price using the calculator
        const priceBreakdown = this.priceCalculator.calculatePrice(stepsData, completedSteps);
        
        // Update price display using PriceDisplay if available
        if (this.priceDisplay) {
            this.priceDisplay.updateDisplay(priceBreakdown);
        } else {
            // Fallback to old method if PriceDisplay isn't initialized
            this.updatePriceDisplay(priceBreakdown.basePrice, priceBreakdown.totalExtras);
        }
        
        // Return the price breakdown
        return priceBreakdown;
    }

    /**
     * Update price display with animation
     * 
     * @param {number} basePrice - Base price
     * @param {number} extras - Extra costs
     */
    updatePriceDisplay(basePrice, extras) {
        const baseEl = this.modal.modal.querySelector('.stc-price-row:first-child span:last-child');
        const extrasEl = this.modal.modal.querySelector('.stc-price-row:nth-child(2) span:last-child');
        const totalEl = this.modal.modal.querySelector('.stc-price-total span:last-child');
    
        // Animate price changes
        if (baseEl) this.animatePrice(baseEl, basePrice);
        if (extrasEl) this.animatePrice(extrasEl, extras, '+');
        if (totalEl) this.animatePrice(totalEl, basePrice + extras);
    }

    /**
     * Animate price change
     * 
     * @param {HTMLElement} element - Element to animate
     * @param {number} newPrice - New price
     * @param {string} prefix - Price prefix
     */
    animatePrice(element, newPrice, prefix = '') {
        const currentPrice = parseFloat(element.textContent.replace(/[^\d.-]/g, '')) || 0;
        const duration = 500; // ms
        const steps = 20;
        const increment = (newPrice - currentPrice) / steps;
        let step = 0;
    
        const animate = () => {
            step++;
            const current = currentPrice + (increment * step);
            element.textContent = `${prefix}$${current.toFixed(2)}`;
    
            if (step < steps) {
                requestAnimationFrame(animate);
            } else {
                element.textContent = `${prefix}$${newPrice.toFixed(2)}`;
            }
        };
    
        requestAnimationFrame(animate);
    }

    /**
     * Get the total price calculation including all extras
     * 
     * @returns {object} Price breakdown
     */
    getPriceBreakdown() {
        // Make sure we have the latest prices
        this.updatePrice();
        
        // Return the most recent calculation from the calculator
        return this.priceCalculator.getLastCalculation();
    }

    /**
     * Export the complete design data for add to cart
     * This collects data from all steps on both sides
     * 
     * @param {boolean} includeDataUrls - Whether to include image data URLs
     * @returns {object} The complete design data
     */
    exportDesign(includeDataUrls = false) {
        if (!this.stepManager || !this.stepController || !this.designExporter) {
            console.error('Cannot export design: required components not initialized');
            return null;
        }
        
        // Update the exporter options
        this.designExporter.options.includeDataUrls = includeDataUrls;
        
        // Get the latest price breakdown
        const priceBreakdown = this.getPriceBreakdown();
        
        // Template info
        const templateInfo = {
            id: this.template.id,
            title: this.template.title,
            dimensions: this.template.config.dimensions
        };
        
        // Export the design
        return this.designExporter.exportDesign(templateInfo, priceBreakdown);
    }

    /**
     * Get complete design data for export
     * 
     * @returns {object} Complete design data
     */
    getDesignData() {
        const designData = this.exportDesign(true);
        
        if (!designData) {
            return {
                templateId: this.template.id,
                templateTitle: this.template.title,
                error: 'Failed to export design data'
            };
        }
        
        return designData;
    }

    /**
     * Handle add to cart
     */
    async handleAddToCart() {

        // if (!this.hasVisitedBackSide) {
        //     this.showBackSideNotification();
        //     return;
        // }

        try {
            // Lazy-load the AddToCartHandler to avoid circular dependencies
            if (!this.addToCartHandler) {
                const { default: AddToCartHandler } = await import('./AddToCartHandler.js');
                this.addToCartHandler = new AddToCartHandler(this, {
                    formSelector: 'form.cart',
                    nonce: window.octStcCustomizer?.nonce || '',
                    generatePreviews: true,
                    previewWidth: 1200,
                    previewHeight: 1675
                });
                this.addToCartHandler.initialize();

                // Set current edit ID if we're editing existing data
                if (this.template && this.template.edit_id) {
                    this.addToCartHandler.currentEditId = this.template.edit_id;
                    console.log('🎯 [_stc_edit] Set currentEditId for existing data:', this.template.edit_id);
                }
            }
            
            // Process the add to cart request
            const result = await this.addToCartHandler.processAddToCart( this.getDesignData() );
            
            if (result) {
                // Success will be handled by the form submission redirect
                console.log('Add to cart process completed successfully');
            }
            
        } catch (error) {
            console.error('Error handling add to cart:', error);
            alert(`Error: ${error.message || 'Unable to add to cart. Please try again.'}`);
        }
    }

    getBackSideIcon() {
        return `
            <div class="stc-back-notification-icon">
                <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                    <line x1="9" y1="3" x2="9" y2="21"></line>
                    <path d="M13 8l4 4-4 4"></path>
                </svg>
            </div>
        `;
    }

    // Get the arrow icon for the button
    getArrowRightIcon() {
        return `
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M5 12h14"></path>
                <path d="M12 5l7 7-7 7"></path>
            </svg>
        `;
    }

    showBackSideNotification() {
        // Create notification if it doesn't exist
        if (!this.backSideNotification) {
            this.backSideNotification = new NotificationPopup({
                title: 'Did you know?',
                message: `${this.getBackSideIcon()}Your trading card has a back side that you can also customize. Would you like to check it out before adding to cart?`,
                popupClass: 'stc-back-notification-popup',
                primaryButton: {
                    text: `View Back Side ${this.getArrowRightIcon()}`,
                    className: 'stc-back-notification-primary',
                    action: () => {
                        // Change to back side
                        const sideSwitch = this.modal.modal.querySelector('.stc-side-switch button[data-side="back"]');
                        if (sideSwitch) {
                            sideSwitch.click();
                        } else {
                            // Fallback if button not found
                            this.handleSideChange('back');
                        }
                    }
                },
                secondaryButton: {
                    text: 'Continue Anyway',
                    action: () => {
                        // Mark as visited to prevent showing again
                        this.hasVisitedBackSide = true;
                        
                        // Trigger add to cart again
                        setTimeout(() => {
                            this.handleAddToCart();
                        }, 100);
                    }
                }
            });
        }
        
        // Show the notification
        this.backSideNotification.show();
    }

    /**
     * Load design from cart item for editing
     *
     * @param {string} cartItemKey - Cart item key
     */
    async loadFromCartItem(cartItemKey) {
        try {
            // Don't clear modal container - we need to preserve the structure
            // Just initialize components that are needed
            this.initializeComponents();

            // Call AJAX to load cart item
            const formData = new FormData();
            formData.append('action', 'load_cart_item_for_edit');
            formData.append('nonce', window.octStcCustomizer.nonce);
            formData.append('cart_item_key', cartItemKey);

            const response = await fetch(window.octStcCustomizer.ajax_url, {
                method: 'POST',
                body: formData
            });

            const data = await response.json();

            if (!data.success) {
                throw new Error(data.data || 'Failed to load cart item');
            }

            // Store cart item key for later update
            const template = data.data.template;
            template.cartItemKey = cartItemKey;
            template.editMode = true;

            // Load template directly (config is already in template format)
            // Note: handleTemplateSelected will show the customization interface
            // which initializes all the necessary components
            await this.handleTemplateSelected(template);

        } catch (error) {
            console.error('❌ [EDIT_MODE] Error loading cart item:', error);
            alert('Failed to load design for editing. Please try again.');

            // Redirect back to product page or cart
            window.location.href = window.location.pathname;
        }
    }

    /**
     * Fetch cart item data without loading into designer
     * Used to determine which mode (simple form vs designer) to use
     *
     * @param {string} cartItemKey - Cart item key
     * @returns {Promise<Object>} Cart item data
     */
    async fetchCartItemData(cartItemKey) {
        try {
            const formData = new FormData();
            formData.append('action', 'load_cart_item_for_edit');
            formData.append('nonce', window.octStcCustomizer.nonce);
            formData.append('cart_item_key', cartItemKey);

            const response = await fetch(window.octStcCustomizer.ajax_url, {
                method: 'POST',
                body: formData
            });

            const data = await response.json();

            if (!data.success) {
                throw new Error(data.data || 'Failed to load cart item');
            }

            return data.data;
        } catch (error) {
            console.error('❌ [FETCH_CART_DATA] Error:', error);
            throw error;
        }
    }

    /**
     * Load cart item in simple form mode
     * Closes designer modal and dispatches event for simple form to handle
     *
     * @param {string} cartItemKey - Cart item key
     * @param {Object} cartItemData - Cart item data
     */
    async loadInSimpleFormMode(cartItemKey, cartItemData) {
        console.log('📝 [LOAD_SIMPLE_FORM] ==========================================');
        console.log('📝 [LOAD_SIMPLE_FORM] Loading simple form mode');
        console.log('📝 [LOAD_SIMPLE_FORM] Cart item key:', cartItemKey);
        console.log('📝 [LOAD_SIMPLE_FORM] ==========================================');

        // Close designer modal if open
        console.log('📝 [LOAD_SIMPLE_FORM] Hiding designer modal...');
        this.modal.hide();

        // Dispatch event to simple form controller to load data
        console.log('📝 [LOAD_SIMPLE_FORM] Dispatching event: stc:simple-form:load-edit');
        const event = new CustomEvent('stc:simple-form:load-edit', {
            detail: {
                cartItemKey: cartItemKey,
                cartData: cartItemData
            }
        });

        document.dispatchEvent(event);
        console.log('📝 [LOAD_SIMPLE_FORM] Event dispatched successfully');

        // Scroll to simple form section
        setTimeout(() => {
            console.log('📝 [LOAD_SIMPLE_FORM] Scrolling to simple form section...');
            const simpleFormSection = document.querySelector('.stc-simple-form-section');

            if (simpleFormSection) {
                console.log('📝 [LOAD_SIMPLE_FORM] Simple form section found, scrolling...');
                simpleFormSection.scrollIntoView({ behavior: 'smooth', block: 'start' });

                // Add temporary highlight effect
                simpleFormSection.style.transition = 'background-color 0.3s';
                simpleFormSection.style.backgroundColor = '#fffacd';
                setTimeout(() => {
                    simpleFormSection.style.backgroundColor = '';
                }, 2000);

                console.log('✅ [LOAD_SIMPLE_FORM] Simple form section highlighted');
            } else {
                console.warn('⚠️ [LOAD_SIMPLE_FORM] Simple form section NOT FOUND');
            }
        }, 100);
    }

    /**
     * Reset designer to initial state
     */
    resetDesigner() {
        this.currentState = 'template-selection';
        this.template = null;
        this.hasVisitedBackSide = false;

        // Reset price calculator
        this.priceCalculator = new PriceCalculator({
            basePrice: this.options.basePrice
        });

        // Show template selector
        if (this.templateSelector) {
            this.templateSelector.show();
        }

        // Hide customization interface
        const sideSwitch = this.modal.modal.querySelector('.stc-side-switch');
        if (sideSwitch) sideSwitch.style.display = 'none';

        const stepsContainer = this.modal.modal.querySelector('.stc-steps');
        if (stepsContainer) stepsContainer.style.display = 'none';

        // Clean up components
        if (this.stepController) {
            this.stepController.disposeHandlers();
        }

        if (this.canvas) {
            this.canvas.destroy();
            this.canvas = null;
        }

        if (this.infoIconManager) {
            this.infoIconManager.dispose();
            this.infoIconManager = null;
        }

        if (this.backSideNotification) {
            this.backSideNotification.dispose();
            this.backSideNotification = null;
        }

        // Clear design exporter
        this.designExporter = null;
    }

    /**
     * Show background removal notification
     * @param {object} data - Data from the event
     */
    showBackgroundRemovalNotification(data) {
        console.log('Showing background removal notification:', data);

        const { handler, info } = data;
        
        // Create notification if it doesn't exist
        if (!this.backgroundRemovalNotification) {
            this.backgroundRemovalNotification = new NotificationPopup({
                title: info?.title || 'Professional Background Removal',
                message: `
                    <div class="stc-background-removal-info">
                        <p>We've detected you uploaded an image for your trading card!</p>
                        <div class="stc-background-removal-examples">
                            <div class="stc-example">
                                <img src="/wp-content/plugins/octo-stc/public/assets/examples/ai-transform-original.webp" alt="Original image">
                                <span>Original Image</span>
                            </div>
                            <div class="stc-example-arrow">→</div>
                            <div class="stc-example">
                                <img src="/wp-content/plugins/octo-stc/public/assets/examples/ai-transform-result.webp" alt="After processing">
                                <span>After Background Removal</span>
                            </div>
                        </div>
                        <p>For professional results, our team will remove the background from your image during production.</p>
                        <p><strong>Note:</strong> If your image already has a transparent background, you can disable this option.</p>
                    </div>
                `,
                popupClass: 'stc-background-removal-notification',
                primaryButton: {
                    text: `Process My Image (Recommended)`,
                    className: 'stc-background-notification-primary',
                    action: () => {
                        // Set background removal to true
                        const stepData = handler.getStepData() || {};
                        stepData.needsBackgroundRemoval = true;
                        handler.setStepData(stepData);
                        handler.rebuildPanel();
                    }
                },
                secondaryButton: {
                    text: 'My Image Already Has No Background',
                    action: () => {
                        // Set background removal to false
                        const stepData = handler.getStepData() || {};
                        stepData.needsBackgroundRemoval = false;
                        handler.setStepData(stepData);
                        handler.rebuildPanel();
                    }
                }
            });
        } else {
            // Update existing notification if needed
            this.backgroundRemovalNotification.update({
                title: info?.title || 'Professional Background Removal'
            });
        }
        
        // Show the notification
        this.backgroundRemovalNotification.show();
    }
    
}